import { AuthContext } from "@beeldit/user-and-access/auth/AuthContext";
import { useContext, useEffect, useRef, useState } from "react";
import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { TenantAndCommerceContext } from "@main/contexts/TenantAndCommerceContext";
import { useSelectedCustomer } from "@main/contexts/SelectedCustomerContext";
import { getApi } from "@beeldit/core/helpers/api";
import useGetCommerceAction from "@main/commerces/actions/get-config/useGetCommerceAction";
import useGetTenantAction from "@main/tenants/actions/get/useGetTenantAction";
import { getCommerceLogo } from "@main/commerces/utils/applyCommerceTheme";

import { Calendar, Gear, User } from "@phosphor-icons/react";

import "./CustomerPanel.css"; // Para estilos adicionales

function CustomerPanel() {
  const { tokenLoaded, user } = useContext(AuthContext);
  const { setTenantId, setCommerceId } = useContext(TenantAndCommerceContext);
  const { selectedCustomer, setSelectedCustomer } = useSelectedCustomer();
  const [customers, setCustomers] = useState<any[]>([]);
  const { getCommerceFunction } = useGetCommerceAction();
  const { getTenantFunction } = useGetTenantAction();

  let { tenantId, commerceId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Establecer valores por defecto si no vienen en la ruta
    let _tenantId = tenantId;
    let _commerceId = commerceId;
    if (!_tenantId) _tenantId = "1";
    if (!_commerceId) _commerceId = "1";
    setTenantId(parseInt(_tenantId));
    setCommerceId(parseInt(_commerceId));
    getApi().defaults.headers.common["tenantId"] = _tenantId;
    getApi().defaults.headers.common["commerceId"] = _commerceId;
  }, [tenantId, commerceId, setTenantId, setCommerceId]);

  useEffect(() => {
    // Cargar pacientes del usuario solo al cargar el usuario
    const fetchCustomers = async () => {
      try {
        const resp = await getApi().get("/user/customers");
        if (resp && resp.data) {
          setCustomers(resp.data);
          // Si no hay seleccionado, seleccionar el primero
          if (!selectedCustomer && resp.data.length) {
            setSelectedCustomer(resp.data[0]);
          }
        }
      } catch (e) {
        // ignore
      }
    };
    if (tokenLoaded) fetchCustomers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tokenLoaded, selectedCustomer, setSelectedCustomer]);

  const lastFetchedRef = useRef<{ tenantId?: string; commerceId?: string }>({});
  // Cargar commerce y tenant cuando cambien, evitando bucles
  useEffect(() => {
    if (!tenantId || !commerceId || !tokenLoaded) return;
    const alreadyFetched =
      lastFetchedRef.current.tenantId === tenantId &&
      lastFetchedRef.current.commerceId === commerceId;
    if (alreadyFetched) return;
    lastFetchedRef.current = { tenantId, commerceId };
    getCommerceFunction(parseInt(commerceId));
    getTenantFunction(parseInt(tenantId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commerceId, tokenLoaded, tenantId]);

  if (!tokenLoaded || !user) {
    return <div>Loading...</div>;
  }

  const bottomTabs = [
    // 1) Próximas sesiones
    {
      icon: <Calendar size={24} />,
      path: `/customer-panel/${tenantId}/${commerceId}/future-sessions`,
      label: "Próximas sesiones",
    },
    // 2) Sesiones pasadas
    {
      icon: <Calendar size={24} />,
      path: `/customer-panel/${tenantId}/${commerceId}/last-sessions`,
      label: "Sesiones pasadas",
    },
    // 3) Contacto
    {
      icon: <User size={24} />,
      path: `/customer-panel/${tenantId}/${commerceId}/contact`,
      label: "Contacto",
    },
    // 4) Cerrar sesión
    {
      icon: <Gear size={24} />,
      path: `/customer-panel/${tenantId}/${commerceId}/logout`,
      label: "Cerrar sesión",
    },
  ];

  return (
    <div className="app-container">
      <header
        className="app-header"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <img src={getCommerceLogo()} alt="Logo" width="200" />
        <div className="customer-selector-container">
          <select
            id="customer-selector"
            className="customer-selector-select"
            value={selectedCustomer ? selectedCustomer.id : ""}
            onChange={(e) => {
              const id = e.target.value;
              const found = customers.find((c) => String(c.id) === String(id));
              setSelectedCustomer(found || null);
            }}
          >
            {customers.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name + " " + c.first_lastname}
              </option>
            ))}
          </select>
        </div>
      </header>
      <div className="app-content">
        <Outlet />
      </div>
      <nav className="bottom-nav">
        {bottomTabs.map((tab, index) =>
          tab.label === "Cerrar sesión" ? (
            <button
              key={index}
              className={`bottom-nav-button ${
                location.pathname === tab.path ? "active" : ""
              }`}
              onClick={() => {
                localStorage.removeItem("token");
                localStorage.removeItem("user");
                localStorage.removeItem("selectedCustomer");
                navigate("/login");
              }}
            >
              {tab.icon}
              <span className="label">{tab.label}</span>
            </button>
          ) : (
            <button
              key={index}
              className={`bottom-nav-button ${
                location.pathname === tab.path ? "active" : ""
              }`}
              onClick={() => navigate(tab.path)}
            >
              {tab.icon}
              <span className="label">{tab.label}</span>
            </button>
          )
        )}
      </nav>
    </div>
  );
}

export default CustomerPanel;
